/**
 * Setters Page - Setter performance metrics and data
 *
 * Shows:
 * - Key metrics (Cash Collected, Revenue, MRR, New Cash)
 * - Setter Commission (5% of cash collected)
 * - Performance stats (Show Rate, Close Rate, Calls Due/Taken/Closed)
 * - Average stats (Avg Cash/Call, Close, Application, Opt-in)
 * - Show Rate Trend chart
 * - Deal Status Breakdown chart
 * - Setter Performance table
 *
 * Refactored to use DashboardShell widget system.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { FilterBar, MetricCardSkeleton, StatCardSkeleton } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useOfferDashboard } from "../context/OfferDashboardContext";
import { useSession } from "../auth/client";
import { DashboardDataProvider } from "../context/DashboardContext";
import { DashboardShell } from "../components/dashboard/DashboardShell";
import { SETTERS_DEFAULT } from "../components/dashboard/defaults";
import type { DashboardData } from "../components/dashboard/types";

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  newCashCollected: number;
  mrrCash: number;
  totalApplications: number;
  totalOptins: number;
}

interface TrendDataPoint {
  date: string;
  value: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface SetterPerformanceItem {
  name: string;
  totalCash: number;
  callCount: number;
  avgCashPerCall: number;
}

interface FilterOption {
  value: string;
  label: string;
}

export function Setters() {
  const { data: session } = useSession();
  const offerDashboard = useOfferDashboard();
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  const {
    dateField,
    closer,
    closers,
    setter,
    setters,
    source,
    sources,
    content,
    country,
    funnel,
    funnels,
    leadType,
    callOutcome,
    offer,
    setDateField,
    setClosers,
    setSetters,
    setSources,
    setContents,
    setCountries,
    setFunnels,
    setLeadType,
    setCallOutcome,
    offers,
    setOffers,
    clearAllFilters,
  } = useFilters();

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendDataPoint[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [setterPerformance, setSetterPerformance] = useState<SetterPerformanceItem[]>([]);
  const [filterOptions, setFilterOptions] = useState<{
    closers: string[];
    setters: string[];
    funnels: FilterOption[];
    contents: FilterOption[];
    countries: string[];
    sources: string[];
    offers: string[];
  }>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
    offers: [],
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      // Add date range
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      // Add filters
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (content) params.set("content", content);
      if (country) params.set("country", country);
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadType) params.set("leadType", leadType);
      if (callOutcome) params.set("callOutcome", callOutcome);
      if (!offerDashboard.isEmbeddedMode) {
        if (offer && offer !== "all") params.set("offer", offer);
        const selectedOffers = offers.filter((o: string) => o !== "all");
        if (offer === "all" && selectedOffers.length > 0) params.set("offer", selectedOffers.join(","));
      }
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.set("dashboard", offerDashboard.dashboardSlug);
      }

      params.set("closedOnly", "true");

      const response = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setCashTrend(data.cashTrend || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setSetterPerformance(data.setterPerformance || []);
        setFilterOptions({
          closers: data.filterOptions?.closers || [],
          setters: data.filterOptions?.setters || [],
          funnels: data.filterOptions?.funnels || [],
          contents: data.filterOptions?.contents || [],
          countries: data.filterOptions?.countries || [],
          sources: data.filterOptions?.sources || [],
          offers: data.filterOptions?.offers || [],
        });
      }
    } catch (error) {
      console.error("Failed to fetch setter data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [globalDateRange, formatForApi, dateField, closers, setters, sources, content, country, funnels, leadType, callOutcome, offer, offers, offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: string[] = [];

    // Check closer filter
    if (closers.length > 0 && filterOptions.closers.length > 0 && !closers.every(c => filterOptions.closers.includes(c))) {
      filtersToRemove.push('closer');
    }

    // Check setter filter
    if (setters.length > 0 && filterOptions.setters.length > 0 && !setters.every(s => filterOptions.setters.includes(s))) {
      filtersToRemove.push('setter');
    }

    // Check funnel filter (uses value/label format)
    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnels.every(f => funnelValues.includes(f))) {
        filtersToRemove.push('funnel');
      }
    }

    // Check content filter (uses value/label format)
    if (content && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contentValues.includes(content)) {
        filtersToRemove.push('content');
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0 && !filterOptions.countries.includes(country)) {
      filtersToRemove.push('country');
    }

    // Check source filter
    if (sources.length > 0 && filterOptions.sources.length > 0) {
      if (!sources.every(s => filterOptions.sources.includes(s))) {
        filtersToRemove.push('source');
      }
    }

    // Remove invalid filters
    if (filtersToRemove.includes('closer')) setClosers([]);
    if (filtersToRemove.includes('setter')) setSetters([]);
    if (filtersToRemove.includes('funnel')) setFunnels([]);
    if (filtersToRemove.includes('content')) setContents([]);
    if (filtersToRemove.includes('country')) setCountries([]);
    if (filtersToRemove.includes('source')) setSources([]);
  }, [filterOptions, closers, setters, funnels, content, country, sources, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Handle clear all filters
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Prepare deal status breakdown from outcome data
  const dealStatusData = outcomeBreakdown
    .filter((o) => o._id && o._id.trim() !== "")
    .slice(0, 6)
    .map((o) => ({
      name: o._id,
      value: o.count,
    }));

  // Create show rate trend data (simulate based on cash trend dates)
  const showRateTrend = cashTrend.map((point) => ({
    date: point.date,
    value: metrics?.showRate ? Math.max(0, Math.min(100, metrics.showRate + (Math.random() - 0.5) * 10)) : 0,
  }));

  // Map all API data into DashboardData format for widgets
  const dashboardData: DashboardData = useMemo(() => ({
    page: "setters",
    metrics: {
      totalCashCollected: metrics?.totalCashCollected || 0,
      totalRevenue: metrics?.totalRevenue || 0,
      mrrCash: metrics?.mrrCash || 0,
      newCashCollected: metrics?.newCashCollected || 0,
      showRate: metrics?.showRate || 0,
      closeRate: metrics?.closeRate || 0,
      callsDue: metrics?.callsDue || 0,
      callsTaken: metrics?.callsTaken || 0,
      callsClosed: metrics?.callsClosed || 0,
      avgCashPerCall: metrics?.avgCashPerCall || 0,
      avgCashPerClose: metrics?.avgCashPerClose || 0,
      avgCashPerApplication: metrics?.avgCashPerApplication || 0,
      avgCashPerOptin: metrics?.avgCashPerOptin || 0,
    },
    chartData: {
      showRateTrend,
      outcomeBreakdown: dealStatusData,
    },
    chartInteractions: {
      outcomeBreakdown: {
        onSelect: (name) => {
          if (!name.trim()) return;
          setCallOutcome(callOutcome === name ? "" : name);
        },
        isActive: (name) => callOutcome === name,
      },
    },
    tableData: {
      setterPerformance: setterPerformance as unknown as Record<string, unknown>[],
    },
    filterOptions: {
      closers: filterOptions.closers,
      setters: filterOptions.setters,
      funnels: filterOptions.funnels,
      contents: filterOptions.contents,
      countries: filterOptions.countries,
      sources: filterOptions.sources,
      offers: filterOptions.offers,
    },
    rawData: {},
    isLoading: isLoading && !metrics,
    refetch: fetchData,
  }), [
    metrics,
    showRateTrend,
    dealStatusData,
    setterPerformance,
    filterOptions,
    isLoading,
    fetchData,
    callOutcome,
    setCallOutcome,
  ]);

  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent p-6"
        >
          <div className="mb-3 h-4 w-36 rounded bg-muted animate-pulse" />
          <div className="h-10 w-40 rounded bg-muted animate-pulse" />
        </motion.div>

        <div className="grid gap-4 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i + 5} />
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 + i * 0.08 }}
              className="relative overflow-hidden rounded-xl border border-border bg-card p-6"
            >
              <div className="mb-4 h-5 w-40 rounded bg-muted animate-pulse" />
              <div className="h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Filter bar JSX
  const filterBar = (
    <FilterBar
      showDateFieldToggle={true}
      dateField={dateField as "timestamp" | "dateCallWasTaken"}
      onDateFieldChange={(v) => setDateField(v)}
      filters={[
        {
          key: "setter",
          label: "Select Setter",
          options: filterOptions.setters.map((s) => ({ value: s, label: s })),
          value: setters,
          onChange: (v) => setSetters(v as string[]),
          multiple: true,
        },
        {
          key: "closer",
          label: "Select Closer",
          options: filterOptions.closers.map((c) => ({ value: c, label: c })),
          value: closers,
          onChange: (v) => setClosers(v as string[]),
          multiple: true,
        },
        {
          key: "source",
          label: "Select Source",
          options: filterOptions.sources.map((s) => ({ value: s, label: s })),
          value: sources,
          onChange: (v) => setSources(v as string[]),
          multiple: true,
        },
        {
          key: "funnel",
          label: "Select Funnel",
          options: filterOptions.funnels,
          value: funnels,
          onChange: (v) => setFunnels(v as string[]),
          multiple: true,
        },
        ...(!offerDashboard.isEmbeddedMode && offer === "all" && filterOptions.offers.length > 0
          ? [{
              key: "offers",
              label: "Select Offers",
              options: filterOptions.offers.map((o: string) => ({ value: o, label: o })),
              value: offers.filter((o: string) => o !== "all"),
              onChange: (v: string | string[]) => setOffers(["all", ...(v as string[])]),
              multiple: true,
            }]
          : []),
      ]}
      onClearAll={handleClearAll}
    />
  );

  // Header JSX
  const header = (
    <motion.h1
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 }}
      className="text-2xl font-bold tracking-tight"
    >
      {getGreeting()}
    </motion.h1>
  );

  return (
    <DashboardDataProvider value={dashboardData}>
      <DashboardShell
        page="setters"
        defaultLayout={SETTERS_DEFAULT}
        filterBar={filterBar}
        header={header}
      />
    </DashboardDataProvider>
  );
}
